<!--
  BaseDocumentViewer
  A reusable modal component for previewing various document types inline.

  SUPPORTED FILE TYPES:
  - PDF (.pdf)
  - Images (.png, .jpg, .jpeg, .gif, .webp)
  - CSV (.csv)
  - Word Documents (.docx)
  - Excel Spreadsheets (.xlsx, .xls)

  PROPS:
  | Prop          | Type        | Default    | Description                                                    |
  |---------------|-------------|------------|----------------------------------------------------------------|
  | visible       | Boolean     | false      | Controls modal visibility. Use with v-model for two-way binding. |
  | blobData      | ArrayBuffer | null       | The raw file data as an ArrayBuffer (e.g., from fetch response). |
  | fileName      | String      | 'Document' | Display name shown in the modal header.                        |
  | fileExtension | String      | ''         | File extension (e.g., '.pdf', 'xlsx'). Falls back to fileName extension if not provided. |
  | loading       | Boolean     | false      | Shows a loading spinner inside the modal when true.            |

  EVENTS:
  - update:visible: Emitted when the modal should close. Use v-model:visible for two-way binding.

  USAGE EXAMPLE:
  <BaseDocumentViewer
    v-model:visible="showViewer"
    :blob-data="documentData"
    file-name="Invoice_2024.pdf"
    file-extension="pdf"
  />

  NOTES:
  - The component uses Teleport to render the modal at the document body level.
  - Clicking outside the modal (on the overlay) will close it.
  - For Excel files with multiple sheets, tabs are displayed for navigation.
  - Requires 'mammoth' and 'xlsx' packages to be installed for DOCX and Excel support.
-->
<template>
  <Teleport to="body">
    <div v-if="visible" class="document-viewer-overlay" @click.self="close">
      <div class="document-viewer-modal">
        <!-- Header -->
        <div class="document-viewer-header">
          <span class="document-viewer-title" :title="fileName">{{ fileName }}</span>
          <button class="document-viewer-close" @click="close" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="document-viewer-content">
          <!-- Loading State -->
          <div v-if="loading" class="document-viewer-loading">
            <div class="spinner"></div>
            <p>Loading document...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="errorMessage" class="document-viewer-error">
            <i class="fas fa-exclamation-triangle"></i>
            <p>{{ errorMessage }}</p>
          </div>

          <!-- PDF Viewer -->
          <div v-else-if="fileType === 'pdf'" class="document-viewer-pdf">
            <object
              :data="blobUrl"
              type="application/pdf"
              width="100%"
              height="100%"
            >
              <iframe :src="blobUrl" width="100%" height="100%">
                <p>Your browser does not support PDF viewing. Please download the file.</p>
              </iframe>
            </object>
          </div>

          <!-- Image Viewer -->
          <div v-else-if="fileType === 'image'" class="document-viewer-image">
            <img :src="blobUrl" :alt="fileName" />
          </div>

          <!-- CSV Viewer -->
          <div v-else-if="fileType === 'csv'" class="document-viewer-table">
            <table class="data-table">
              <thead v-if="csvData.length > 0">
                <tr>
                  <th v-for="(header, index) in csvData[0]" :key="index">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in csvData.slice(1)" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- DOCX Viewer -->
          <div v-else-if="fileType === 'docx'" class="document-viewer-docx">
            <div v-html="docxHtml" class="docx-content"></div>
          </div>

          <!-- Excel Viewer -->
          <div v-else-if="fileType === 'excel'" class="document-viewer-excel">
            <!-- Sheet Tabs -->
            <div v-if="excelSheets.length > 1" class="sheet-tabs">
              <button
                v-for="(sheet, index) in excelSheets"
                :key="index"
                :class="['sheet-tab', { active: activeSheet === index }]"
                @click="activeSheet = index"
              >
                {{ sheet.name }}
              </button>
            </div>
            <!-- Sheet Content -->
            <div class="document-viewer-table">
              <table v-if="currentSheetData.length > 0" class="data-table">
                <thead>
                  <tr>
                    <th v-for="(header, index) in currentSheetData[0]" :key="index">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in currentSheetData.slice(1)" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell ?? '' }}</td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="empty-sheet">This sheet is empty.</p>
            </div>
          </div>

          <!-- Unsupported Format -->
          <div v-else class="document-viewer-unsupported">
            <i class="fas fa-file"></i>
            <p>Preview not available for this file type.</p>
            <p class="file-info">{{ fileName }}</p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import DOMPurify from 'dompurify';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  blobData: {
    type: ArrayBuffer,
    default: null,
  },
  fileName: {
    type: String,
    default: 'Document',
  },
  fileExtension: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible']);

// Reactive state
const errorMessage = ref(null);
const blobUrl = ref(null);
const csvData = ref([]);
const docxHtml = ref('');
const excelSheets = ref([]);
const activeSheet = ref(0);

// Computed properties
const fileType = computed(() => {
  // Use provided extension as primary source, fall back to extracting from fileName
  let ext = props.fileExtension?.toLowerCase()?.replace('.', '') || '';
  if (!ext && props.fileName) {
    ext = props.fileName.split('.').pop().toLowerCase();
  }
  if (!ext) return 'unknown';
  
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'csv') return 'csv';
  if (ext === 'docx') return 'docx';
  if (['xlsx', 'xls'].includes(ext)) return 'excel';
  
  return 'unknown';
});

const currentSheetData = computed(() => {
  if (excelSheets.value.length === 0) return [];
  return excelSheets.value[activeSheet.value]?.data || [];
});

// Methods
function close() {
  emit('update:visible', false);
}

function cleanup() {
  if (blobUrl.value) {
    URL.revokeObjectURL(blobUrl.value);
    blobUrl.value = null;
  }
  csvData.value = [];
  docxHtml.value = '';
  excelSheets.value = [];
  activeSheet.value = 0;
  errorMessage.value = null;
}

function getMimeType() {
  const mimeTypes = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    csv: 'text/csv',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
  };
  // Use provided extension as primary source, fall back to extracting from fileName
  let ext = props.fileExtension?.toLowerCase()?.replace('.', '') || '';
  if (!ext && props.fileName) {
    ext = props.fileName.split('.').pop().toLowerCase();
  }
  return mimeTypes[ext] || 'application/octet-stream';
}

function createBlobUrl() {
  const blob = new Blob([props.blobData], { type: getMimeType() });
  blobUrl.value = URL.createObjectURL(blob);
}

async function parseCSV() {
  const decoder = new TextDecoder('utf-8');
  const text = decoder.decode(props.blobData);
  
  // Simple CSV parsing (handles basic cases)
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  csvData.value = lines.map(line => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  });
}

async function parseDOCX() {
  const result = await mammoth.convertToHtml({ arrayBuffer: props.blobData });
  // Sanitize HTML to prevent XSS attacks from malicious DOCX files
  docxHtml.value = DOMPurify.sanitize(result.value);
  
  if (result.messages && result.messages.length > 0) {
    console.warn('DOCX conversion warnings:', result.messages);
  }
}

async function parseExcel() {
  const workbook = XLSX.read(props.blobData, { type: 'array' });
  
  excelSheets.value = workbook.SheetNames.map(name => {
    const sheet = workbook.Sheets[name];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    return { name, data };
  });
  
  activeSheet.value = 0;
}

async function processDocument() {
  cleanup();
  errorMessage.value = null;

  try {
    switch (fileType.value) {
      case 'pdf':
      case 'image':
        createBlobUrl();
        break;
      case 'csv':
        await parseCSV();
        break;
      case 'docx':
        await parseDOCX();
        break;
      case 'excel':
        await parseExcel();
        break;
      default:
        errorMessage.value = 'Unsupported file format';
    }
  } catch (error) {
    console.error('Error processing document:', error);
    errorMessage.value = 'Failed to load document. Please try downloading instead.';
  }
}

// Watchers
watch(() => props.visible, (newVal) => {
  if (newVal && props.blobData) {
    processDocument();
  } else if (!newVal) {
    cleanup();
  }
});

watch(() => props.blobData, (newVal) => {
  if (props.visible && newVal) {
    processDocument();
  }
});

// Lifecycle hooks
onBeforeUnmount(() => {
  cleanup();
});
</script>

<style lang="scss" scoped>
.document-viewer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.document-viewer-modal {
  background: white;
  border-radius: 12px;
  width: 90vw;
  max-width: 1200px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.document-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.document-viewer-title {
  font-weight: 600;
  font-size: 16px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 50px);
}

.document-viewer-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
    color: #1f2937;
  }
}

.document-viewer-content {
  flex: 1;
  overflow: auto;
  position: relative;
}

// Loading State
.document-viewer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  p {
    margin-top: 16px;
    font-size: 14px;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Error State
.document-viewer-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #ef4444;
  
  i {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  p {
    font-size: 14px;
    color: #6b7280;
  }
}

// PDF Viewer
.document-viewer-pdf {
  height: 100%;
  
  object, iframe {
    border: none;
  }
}

// Image Viewer
.document-viewer-image {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  background: #f3f4f6;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
}

// Table Viewer (CSV & Excel)
.document-viewer-table {
  padding: 16px;
  overflow: auto;
  height: 100%;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th, td {
    padding: 10px 12px;
    text-align: left;
    border: 1px solid #e5e7eb;
    white-space: nowrap;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  tbody tr:nth-child(even) {
    background: #f9fafb;
  }
  
  tbody tr:hover {
    background: #eff6ff;
  }
}

// DOCX Viewer
.document-viewer-docx {
  padding: 24px 32px;
  
  .docx-content {
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.6;
    
    :deep(h1), :deep(h2), :deep(h3), :deep(h4), :deep(h5), :deep(h6) {
      margin-top: 1.5em;
      margin-bottom: 0.5em;
      font-weight: 600;
    }
    
    :deep(p) {
      margin-bottom: 1em;
    }
    
    :deep(table) {
      border-collapse: collapse;
      width: 100%;
      margin: 1em 0;
      
      td, th {
        border: 1px solid #e5e7eb;
        padding: 8px;
      }
    }
    
    :deep(img) {
      max-width: 100%;
      height: auto;
    }
  }
}

// Excel Sheet Tabs
.sheet-tabs {
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
}

.sheet-tab {
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-bottom: none;
  background: white;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
}

.document-viewer-excel {
  display: flex;
  flex-direction: column;
  height: 100%;
  
  .document-viewer-table {
    flex: 1;
    overflow: auto;
  }
}

.empty-sheet {
  text-align: center;
  color: #6b7280;
  padding: 40px;
}

// Unsupported Format
.document-viewer-unsupported {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
  
  i {
    font-size: 64px;
    margin-bottom: 16px;
    color: #d1d5db;
  }
  
  p {
    font-size: 16px;
    margin: 4px 0;
  }
  
  .file-info {
    font-size: 13px;
    color: #9ca3af;
  }
}

// Mobile Responsive
@media (max-width: 768px) {
  .document-viewer-overlay {
    padding: 10px;
  }
  
  .document-viewer-modal {
    width: 100%;
    height: 95vh;
    border-radius: 8px;
  }
  
  .document-viewer-header {
    padding: 12px 16px;
  }
  
  .document-viewer-title {
    font-size: 14px;
  }
  
  .data-table {
    font-size: 12px;
    
    th, td {
      padding: 8px;
    }
  }
  
  .document-viewer-docx {
    padding: 16px;
  }
  
  .sheet-tabs {
    padding: 8px 12px;
  }
  
  .sheet-tab {
    padding: 6px 12px;
    font-size: 12px;
  }
}
</style>
